import { call, put, select, takeEvery, takeLatest, delay } from 'redux-saga/effects';
import { MODELS_LIST } from 'src/store/ActionTypes';
import get from 'lodash.get';

import * as api from 'src/store/api';
import CONSTANTS from 'src/store/constants';
import { modelRBSF } from 'src/functions/filters';
import { enqueueNotification } from 'src/store/actions/app';

const selectModels = (state) => state.modelsList.models;

function* fetchModels(action) {
  try {
    const shouldLoadModelStats = +(window.localStorage.getItem('shouldLoadModelStats') ?? 0);
    const siteRole = yield select((state) => state?.app?.profile?.siteRole ?? 'CLIENT_USER');
    const canLoadModelStats = CONSTANTS.ROLES.slice(0, 2).includes(siteRole);
    const {
      payload: { fields = '*', ...query },
    } = action;
    const options = {
      headers: {
        'X-Fields': fields,
      },
    };
    const models = yield call(api.fetchModels, query, options);
    const segmentTypeMap = CONSTANTS.TAL_MODEL.SEGMENT_TYPE;
    const segmentMap = {};
    // Retrieve segments referenced in the modelData field
    for (const model of models) {
      const dataPayload = get(model, 'modelData.dataPayload', null);
      const id = get(model, 'id');
      if (dataPayload) {
        Object.entries(dataPayload).forEach(([key, value]) => {
          if (key.includes('segment_id') && value) {
            if (!segmentMap[value]) segmentMap[value] = [segmentTypeMap[key], [id]];
            else {
              segmentMap[value][1].push(id);
            }
          }
        });
      }
    }
    const segmentIds = Object.keys(segmentMap);
    const segmentOptions = {
      headers: {
        'X-Fields':
          'items{id,name,filter,matched,refresh{id,name,platform},dataset{name,payload{onb_zero_claims,onb_gte_one_claims,onb_validation_estimate,match_zero_claims,match_gte_one_claims,match_validation_estimate}},clusters{tag,matched}}',
      },
    };
    const segmentQuery = {
      filters: [
        {
          name: 'id',
          op: 'in',
          val: segmentIds,
        },
      ],
    };
    const results = yield call(api.fetchSegmentsExperimental, segmentQuery, segmentOptions);
    // walk the results and set segment properties in the right modelData.dataPayload
    results.forEach((segmentData) => {
      const { id: segmentId } = segmentData;
      const [segmentType, modelIds] = segmentMap[segmentId];
      modelIds.forEach((modelId) => {
        const modelIdx = models.findIndex((model) => +model.id === +modelId);
        models[modelIdx].modelData.dataPayload[segmentType] = segmentData;
      });
    });
    if (canLoadModelStats && shouldLoadModelStats) {
      for (const parentModel of models) {
        const { dataModelType: parentModelType, name: parentModelName, id: parentId, trainingType } = parentModel;
        const parentIdx = models.findIndex((mdl) => mdl.id === parentId);
        const parentFairnessMetadata = parentModel?.modelData?.metadataPayload?.fairness;
        if (
          parentModelType === 'CLASSIFIER' &&
          modelRBSF(parentModel, siteRole) === CONSTANTS.TAL_MODEL.STATE_GROUPS.COMPLETED &&
          !parentFairnessMetadata
        ) {
          try {
            const stats = yield call(api.fetchModelStats, {
              modelId: parentId,
              modelName: parentModelName,
              modelType: parentModelType,
              modelClass: 'Classifier',
              trainingType,
            });
            if (parentIdx !== -1) models[parentIdx].stats = stats;
          } catch {
            // pass
          }
        } else {
          for (const model of parentModel.modelGroup) {
            const { dataModelType, name: modelName, id: modelId } = model;
            const modelFairnessMetadata = model?.modelData?.metadataPayload?.fairness;
            if (modelRBSF(model, siteRole) === CONSTANTS.TAL_MODEL.STATE_GROUPS.COMPLETED && !modelFairnessMetadata) {
              try {
                const stats = yield call(api.fetchModelStats, {
                  modelId,
                  modelName,
                  modelType: dataModelType,
                  modelClass: 'Actuarial',
                  trainingType,
                });
                model.stats = stats;
                const modelIdx = models.findIndex((mdl) => mdl.id === modelId);
                if (modelIdx !== -1 && parentIdx !== -1) models[parentIdx].modelGroup[modelIdx].stats = stats;
              } catch (err) {
                // pass
              }
            }
          }
        }
      }
    }
    yield put({
      type: MODELS_LIST.FETCH_MODELS_SUCCEEDED,
      payload: models,
    });
  } catch (e) {
    yield put({
      type: MODELS_LIST.FETCH_MODELS_FAILED,
    });
  } finally {
    yield delay(500);
    yield put({
      type: MODELS_LIST.UNSET,
      payload: 'fetchModelsStatus',
    });
  }
}

function* selectGridRow(action) {
  const {
    payload: { row, type },
  } = action;
  const antiType = type === 'classifier' ? 'actuarial' : 'classifier';
  const {
    modelsList: { maxGridRowSelection, selectedGridRows },
  } = yield select();
  if (selectedGridRows[antiType].length > 0) {
    yield put({
      type: MODELS_LIST.DESELECT_ALL_MODEL_IDS_BY_TYPE,
      payload: antiType,
    });
  }
  if (selectedGridRows[type].length < maxGridRowSelection[type]) {
    yield put({
      type: MODELS_LIST.SET_SELECTED_GRID_ROWS,
      payload: {
        type,
        payload: [...selectedGridRows[type], row],
      },
    });
  } else {
    const [, ...restRows] = selectedGridRows[type];
    yield put({
      type: MODELS_LIST.SET_SELECTED_GRID_ROWS,
      payload: {
        type,
        payload: [...restRows, row],
      },
    });
  }
}

function* deselectGridRow(action) {
  const {
    payload: { row, type },
  } = action;
  const {
    modelsList: { selectedGridRows },
  } = yield select();
  const filteredSelectedGridRows = selectedGridRows[type].filter(({ id }) => id !== row.id);
  yield put({
    type: MODELS_LIST.SET_SELECTED_GRID_ROWS,
    payload: {
      type,
      payload: filteredSelectedGridRows,
    },
  });
}

function* editModel(action) {
  try {
    const {
      payload: { id, payload },
    } = action;
    yield call(api.updateThinkalikeModel, id, payload);
    yield put({
      type: MODELS_LIST.EDIT_MODEL_SUCCEEDED,
      payload: {
        id,
        payload,
      },
    });
  } catch (e) {
    yield put({
      type: MODELS_LIST.EDIT_MODEL_FAILED,
    });
  } finally {
    yield delay(500);
    yield put({
      type: MODELS_LIST.UNSET,
      payload: 'editModelStatus',
    });
  }
}

function* updateVisibilityForModel(action) {
  let nextHiddenByRoleValue = null;
  try {
    const { payload: id } = action;
    const modelListFlat = [];
    const models = yield select(selectModels);
    models.forEach((model) => {
      modelListFlat.push({ id: model.id, hiddenByRole: model.hiddenByRole, dataModelType: model.dataModelType });
      model.modelGroup.forEach((subModel) =>
        modelListFlat.push({
          id: subModel.id,
          hiddenByRole: subModel.hiddenByRole,
          dataModelType: subModel.dataModelType,
        })
      );
    });
    const { dataModelType, hiddenByRole } = modelListFlat.find((model) => model.id === id);
    nextHiddenByRoleValue = hiddenByRole === null ? 'ADMIN' : null; // only support two states atm, if it's null -> ADMIN or unset
    yield call(api.updateThinkalikeModel, id, { dataModelType, hiddenByRole: nextHiddenByRoleValue }); // hidden for admins and below or unset
    yield put({
      type: nextHiddenByRoleValue ? MODELS_LIST.HIDE_MODEL_SUCCEEDED : MODELS_LIST.SHOW_MODEL_SUCCEEDED,
      payload: action.payload,
    });
  } catch (e) {
    yield put({
      type: nextHiddenByRoleValue ? MODELS_LIST.HIDE_MODEL_FAILED : MODELS_LIST.SHOW_MODEL_FAILED,
    });
  } finally {
    yield delay(500);
    yield put({
      type: MODELS_LIST.UNSET,
      payload: nextHiddenByRoleValue ? 'hideModelStatus' : 'showModelStatus',
    });
  }
}

function* checkDeleteModel(action) {
  try {
    const csList = yield call(api.checkDeleteThinkalikeModel, action.payload);
    yield put({
      type: MODELS_LIST.CHECK_DELETE_MODEL_SUCCEEDED,
      payload: csList,
    });
  } catch (e) {
    yield put({
      type: MODELS_LIST.CHECK_DELETE_MODEL_FAILED,
    });
  } finally {
    yield delay(500);
    yield put({
      type: MODELS_LIST.UNSET,
      payload: 'checkDeleteModelStatus',
    });
  }
}

function* copyModelToClient(action) {
  try {
    yield call(api.copyModelToClient, action.payload);
    yield put({
      type: MODELS_LIST.COPY_MODEL_SUCCEEDED,
    });
    yield put(enqueueNotification('Model copying has begun.', { autoClose: true, timeout: 5000 }));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e.message);
    yield put({
      type: MODELS_LIST.COPY_MODEL_FAILED,
      payload: e.message,
    });
  } finally {
    yield delay(500);
    yield put({
      type: MODELS_LIST.UNSET,
      payload: 'copyModelStatus',
    });
  }
}

function* deleteModel(action) {
  try {
    yield call(api.deleteThinkalikeModel, action.payload);
    yield put({
      type: MODELS_LIST.DELETE_MODEL_SUCCEEDED,
      payload: action.payload,
    });
  } catch (e) {
    yield put({
      type: MODELS_LIST.DELETE_MODEL_FAILED,
    });
  } finally {
    yield delay(500);
    yield put({
      type: MODELS_LIST.UNSET,
      payload: 'deleteModelStatus',
    });
  }
}

function* approveModel(action) {
  try {
    const { id, dataModelType, visibleModelVariation } = action.payload;
    yield call(api.updateThinkalikeModel, id, {
      dataModelType,
      metadataPayload: { visibleModelVariation },
    });
    yield call(api.addThinkalikeModelStatus, id, {
      status: CONSTANTS.TAL_MODEL.STATE.APPROVED,
    });
    yield put({
      type: MODELS_LIST.APPROVE_MODEL_SUCCEEDED,
    });
  } catch (e) {
    yield put({
      type: MODELS_LIST.APPROVE_MODEL_FAILED,
    });
  } finally {
    yield delay(500);
    yield put({
      type: MODELS_LIST.UNSET,
      payload: 'approveModelStatus',
    });
  }
}

function* fetchClientByTenant(action) {
  try {
    const client = yield call(api.fetchClientByTenant, action.payload);
    yield put({
      type: MODELS_LIST.FETCH_CLIENT_BY_TENANT_SUCCEEDED,
      payload: client,
    });
  } catch (e) {
    yield put({
      type: MODELS_LIST.FETCH_CLIENT_BY_TENANT_FAILED,
      payload: e.message || e.toString(),
    });
  } finally {
    delay(500);
    yield put({
      type: MODELS_LIST.UNSET,
      payload: 'fetchClientByTenantStatus',
    });
  }
}

function* fetchDownloadUrl(action) {
  try {
    if (action.type === MODELS_LIST.FETCH_MODEL_DL_FROM_ENTITY) {
      const { dlId, entityId, entityType } = action.payload;
      const downloadUrl = yield call(api.fetchEntityDownloadUrl, entityId, entityType, dlId);
      yield put({
        type: MODELS_LIST.FETCH_MODEL_DL_SUCCEEDED,
        payload: downloadUrl,
      });
    }
    if (action.type === MODELS_LIST.FETCH_MODEL_DL_FROM_META) {
      const { entityId, entryType } = action.payload;
      const downloadUrl = yield call(api.fetchModelDownloadUrl, entityId, entryType);
      yield put({
        type: MODELS_LIST.FETCH_MODEL_DL_SUCCEEDED,
        payload: downloadUrl,
      });
    }
  } catch (e) {
    yield put({
      type: MODELS_LIST.FETCH_MODEL_DL_FAILED,
      payload: e.message || e.toString(),
    });
  } finally {
    delay(500);
    yield put({
      type: MODELS_LIST.UNSET,
      payload: 'fetchModelDownloadStatus',
    });
  }
}

function* fetchModelsSaga() {
  yield takeLatest(MODELS_LIST.FETCH_MODELS, fetchModels);
}

function* onRowSelectSaga() {
  yield takeEvery(MODELS_LIST.SELECT_GRID_ROW, selectGridRow);
}

function* onRowDeselectSaga() {
  yield takeEvery(MODELS_LIST.DESELECT_GRID_ROW, deselectGridRow);
}

function* editModelSaga() {
  yield takeEvery(MODELS_LIST.EDIT_MODEL, editModel);
}

function* hideModelSaga() {
  yield takeEvery([MODELS_LIST.HIDE_MODEL, MODELS_LIST.SHOW_MODEL], updateVisibilityForModel);
}

function* copyModelSaga() {
  yield takeEvery(MODELS_LIST.COPY_MODEL, copyModelToClient);
}

function* checkDeleteModelSaga() {
  yield takeEvery(MODELS_LIST.CHECK_DELETE_MODEL, checkDeleteModel);
}

function* deleteModelSaga() {
  yield takeEvery(MODELS_LIST.DELETE_MODEL, deleteModel);
}

function* approveModelSaga() {
  yield takeEvery(MODELS_LIST.APPROVE_MODEL, approveModel);
}

function* fetchClientByTenantSaga() {
  yield takeEvery(MODELS_LIST.FETCH_CLIENT_BY_TENANT, fetchClientByTenant);
}

function* fetchDownloadUrlSaga() {
  yield takeEvery([MODELS_LIST.FETCH_MODEL_DL_FROM_ENTITY, MODELS_LIST.FETCH_MODEL_DL_FROM_META], fetchDownloadUrl);
}

export default [
  fetchModelsSaga(),
  onRowSelectSaga(),
  onRowDeselectSaga(),
  editModelSaga(),
  hideModelSaga(),
  copyModelSaga(),
  deleteModelSaga(),
  checkDeleteModelSaga(),
  approveModelSaga(),
  fetchClientByTenantSaga(),
  fetchDownloadUrlSaga(),
];
